import React, { useState, useMemo, useRef, useEffect } from "react";
import { Box, TextField, InputAdornment, Checkbox, CircularProgress } from "@mui/material";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";
import SearchIcon from "@mui/icons-material/Search";
import SideBarNavigation from "./SideBarNavigation";
import Header from "./header";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import ChoicesPanel from "./choices";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import EquivalentList from "./equivalent-list";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";
import { AuthorizedWarehouseDto } from "src/api/references/referenceServicesDtos";

// Muadil data için tip tanımı
export interface EquivalentStockData {
  id: number | string;
  name: string;
  current: number;
  unit: string;
  barcode: string;
}

interface StockAddLayoutProps {
  title: string;
  fileProtocol?: string;
  onClose: () => void;
}

const StockAddLayout: React.FC<StockAddLayoutProps> = ({ title, fileProtocol, onClose }) => {
  const { t } = useTranslate();
  const { user, selectedOfficeId: reduxOfficeId } = useAppSelector((state) => state.auth);

  const effectiveOffId = React.useMemo(() => {
    const selectedOffice = user?.UserAssPriviliges?.find((office) => office.ID === reduxOfficeId);
    return Number(selectedOffice?.OFF_ID || user?.UserAssPriviliges?.[0]?.OFF_ID || 0);
  }, [reduxOfficeId, user]);

  const [warehouses, setWarehouses] = useState<AuthorizedWarehouseDto[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState<boolean>(true);
  const [warehousesError, setWarehousesError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadWarehouses = async () => {
      try {
        setWarehousesLoading(true);
        setWarehousesError(null);
        if (!effectiveOffId) {
          setWarehouses([]);
          return;
        }
        const result = await referenceService.referenceList.getAuthorizedWarehouses(effectiveOffId);
        if (!isMounted) return;
        setWarehouses(result || []);
      } catch (error) {
        if (!isMounted) return;
        console.error("Error loading authorized warehouses:", error);
        setWarehousesError("Depo bilgileri yüklenirken hata oluştu.");
        setWarehouses([]);
      } finally {
        if (isMounted) {
          setWarehousesLoading(false);
        }
      }
    };

    loadWarehouses();

    return () => {
      isMounted = false;
    };
  }, [effectiveOffId]);
  const [selectedStocks, setSelectedStocks] = useState<number[]>([]);
  const [showEquivalents, setShowEquivalents] = useState(false);
  const [equivalentData, setEquivalentData] = useState<EquivalentStockData[]>([]);
  const [equivalentSourceId, setEquivalentSourceId] = useState<number | string | null>(null);

  const handleShowEquivalents = (row: any) => {
    // Burada normalde API'den o ilacın muadilleri çekilir
    // Şimdilik dummy data oluşturuyoruz
    const dummyEquivalents: EquivalentStockData[] = [
      { id: `eq-${row.id}-1`, name: `${row.name} (Muadil 1)`, current: 50, unit: row.unit, barcode: "123456789" },
      { id: `eq-${row.id}-2`, name: `${row.name} (Muadil 2)`, current: 30, unit: row.unit, barcode: "987654321" },
    ];
    setEquivalentSourceId(row.id);
    setEquivalentData(dummyEquivalents);
    setShowEquivalents(true);
  };
  const [selectedStocksForPanel, setSelectedStocksForPanel] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Örnek data - gerçek data API'den gelecek
  const rows = useMemo(
    () => [
      {
        id: 1,
        name: "Parol 500 mg Tablet",
        amount: 1,
        current: 120,
        unit: "Kutu",
        hTotal: 10,
        kTotal: 5,
        total: 135,
        doctor: "Dr. Demo",
        depleted: false,
        direction: "",
        outOfPackage: false,
        note: "",
        barcode: "8699514090011",
        stockType: "İlaç",
        expiryDate: "12/2026",
        control: "",
        medulaExpiryDate: "12/2026",
        detailRows: [
          {
            id: "1-d-1",
            kurumAdi: "Kurum #0",
            fiyat: 322.73,
            indirimOrani: 0,
            toplamTutar: 322.73,
            indirimTutari: 0,
            kdv: 32.27,
            tevkifatTutar: 0,
            tevOrani: 0,
            netTutar: 355.0,
          },
          {
            id: "1-d-2",
            kurumAdi: "Kurum #2",
            fiyat: 5000,
            indirimOrani: 0,
            toplamTutar: 5000,
            indirimTutari: 0,
            kdv: 500,
            tevkifatTutar: 0,
            tevOrani: 0,
            netTutar: 5500,
          },
        ],
      },
      {
        id: 2,
        name: "SF 0.9% 100 ml",
        amount: 2,
        current: 45,
        unit: "Flakon",
        hTotal: 0,
        kTotal: 0,
        total: 45,
        doctor: "Dr. Demo",
        depleted: true,
        direction: "",
        outOfPackage: true,
        note: "",
        barcode: "8699514090028",
        stockType: "Sarf",
        expiryDate: "08/2025",
        control: "",
        medulaExpiryDate: "08/2025",
        detailRows: [
          {
            id: "2-d-1",
            kurumAdi: "Kurum #0",
            fiyat: 120.5,
            indirimOrani: 0,
            toplamTutar: 120.5,
            indirimTutari: 0,
            kdv: 12.05,
            tevkifatTutar: 0,
            tevOrani: 0,
            netTutar: 132.55,
          },
          {
            id: "2-d-2",
            kurumAdi: "Kurum #1",
            fiyat: 80,
            indirimOrani: 0,
            toplamTutar: 80,
            indirimTutari: 0,
            kdv: 8,
            tevkifatTutar: 0,
            tevOrani: 0,
            netTutar: 88,
          },
        ],
      },
    ],
    []
  );

  const handleStockToggle = (id: number) => {
    const isRemoving = selectedStocks.includes(id);
    if (isRemoving && equivalentSourceId === id) {
      setEquivalentData([]);
      setEquivalentSourceId(null);
    }

    setSelectedStocks((prev) =>
      prev.includes(id) ? prev.filter((stockId) => stockId !== id) : [...prev, id]
    );
    // Seçilen stokları panel'e ekle
    setSelectedStocksForPanel((prev) =>
      prev.includes(id) ? prev.filter((stockId) => stockId !== id) : [...prev, id]
    );
  };

  const handlePanelItemToggle = (id: number | string) => {
    const numericId = id as number;
    const isRemoving = selectedStocksForPanel.includes(numericId);
    if (isRemoving && equivalentSourceId === numericId) {
      setEquivalentData([]);
      setEquivalentSourceId(null);
    }

    setSelectedStocksForPanel((prev) =>
      prev.includes(numericId) ? prev.filter((stockId) => stockId !== numericId) : [...prev, numericId]
    );
    setSelectedStocks((prev) =>
      prev.includes(numericId) ? prev.filter((stockId) => stockId !== numericId) : [...prev, numericId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStocksForPanel.length === rows.length) {
      setSelectedStocksForPanel([]);
      setSelectedStocks([]);
      setEquivalentData([]);
      setEquivalentSourceId(null);
    } else {
      const allIds = rows.map((row) => row.id);
      setSelectedStocksForPanel(allIds);
      setSelectedStocks(allIds);
    }
  };

  // Seçilen stokların data'sı
  const selectedStocksData = useMemo(() => {
    return rows.filter((row) => selectedStocksForPanel.includes(row.id));
  }, [rows, selectedStocksForPanel]);

  // Grid kolonları
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "select",
        headerName: "",
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={selectedStocks.includes(params.row.id)}
              onChange={() => handleStockToggle(params.row.id)}
              sx={{ color: "#333", "&.Mui-checked": { color: "#333" } }}
            />
            <ByzMuiOpenProcessMenu
              menuItems={[
                {
                  label: "Muadillerini Göster",
                  onClick: () => handleShowEquivalents(params.row),
                },
                {
                  label: "Sık Kullanılanlara Ekle",
                  onClick: () => {
                    // Favorilere ekleme işlemi
                  },
                },
                {
                  label: "Sık Kullanılanlardan Çıkar",
                  onClick: () => {
                    // Favorilerden çıkarma işlemi
                  },
                },
              ]}
              onClose={() => { }}

              sx={{ marginRight: "8px" }}
            />
            <Box
              className="Byz-divider"
              sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "1px", backgroundColor: "#666" }}
            />
          </Box>
        ),
      },
      { field: "name", headerName: t("menus.stock-grid-name"), flex: 1, minWidth: 150 },
      { field: "current", headerName: t("menus.stock-grid-current"), width: 120 },
      { field: "unit", headerName: t("menus.stock-grid-unit"), width: 100 },
      { field: "note", headerName: t("menus.stock-grid-note"), flex: 1, minWidth: 150 },
      { field: "barcode", headerName: t("menus.stock-grid-barcode"), width: 150 },
      { field: "stockType", headerName: t("menus.stock-grid-stock-type"), width: 120 },
      { field: "expiryDate", headerName: t("menus.stock-grid-expiry-date"), width: 120 },
      { field: "control", headerName: t("menus.stock-grid-control"), width: 100 },
      { field: "medulaExpiryDate", headerName: t("menus.stock-grid-medula-expiry-date"), width: 150 },
    ],
    [t, selectedStocks]
  );

  if (warehousesLoading && !warehouses.length) {
    return (
      <Box
        sx={{
          minHeight: "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "90vh",
        maxHeight: "90vh",
        display: "flex",
        transition: "height 0.3s ease-in-out",
        position: "relative",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Sol taraf - Hasta bilgisi ve kategori alanı */}
      <Box sx={{ width: 300, borderRight: 1, borderColor: "divider", p: 2, height: "100%", overflowY: "auto", pb: "100px", boxSizing: "border-box" }}>
        <ByzMuiTypography isTitle sx={{ mb: 3 }}>
          {t("menus.add-stock")}
        </ByzMuiTypography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <ByzMuiTypography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem", mr: 0.8 }}
            >
              {t("patient-request-service.patient-info.name-surname")}
            </ByzMuiTypography>
            <ByzMuiTypography
              variant="body2"
              sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
            >
              {title}
            </ByzMuiTypography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ByzMuiTypography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem", mr: 0.8 }}
            >
              {t("patient-request-service.patient-info.file-protocol")}
            </ByzMuiTypography>
            <ByzMuiTypography
              variant="body2"
              sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
            >
              {fileProtocol || "-"}
            </ByzMuiTypography>
          </Box>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t("patient-request-service.search.placeholder")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        <SideBarNavigation warehouses={warehouses} loading={warehousesLoading} />

        {warehousesError && (
          <Box sx={{ mt: 2 }}>
            <ByzMuiTypography variant="body2" color="error">
              {warehousesError}
            </ByzMuiTypography>
          </Box>
        )}
      </Box>

      {/* Sağ taraf - Yeni Stok Ekle alanı */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 1, sm: 2 },
          position: "relative",
          minWidth: 0,
          height: "100%",
          overflowY: "auto",
          pb: "100px",
          boxSizing: "border-box",
        }}
      >
        <Header onClose={onClose} />

        {/* Grid */}
        <Box sx={{ width: "100%", maxHeight: "55vh", mt: 2 }}>
          <ByzMuiDataGrid
            autoHeight={rows.length < 10} // Satır azsa otomatik yükseklik, çoksa scroll olsun
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#800000 !important",
                color: "#ffffff !important",
                "&:hover": {
                  backgroundColor: "#990000 !important",
                },
                "& .MuiDataGrid-cell": {
                  color: "#ffffff !important",
                },
                // Seçili satırdaki tüm iconlar ve checkbox border'ı beyaz olsun
                "& .MuiCheckbox-root": {
                  color: "#ffffff !important", // Border/Unchecked hali
                },
                "& .MuiCheckbox-root.Mui-checked": {
                  color: "#2196f3 !important", // Seçiliyken mavi renk
                  "& .MuiSvgIcon-root": {
                    // Ortadaki tik işaretini simüle etmek gerekirse bazen filter/contrast gerekebilir 
                    // ama MUI default iconunda color bg'yi temsil eder. 
                    // Eğer default MUI checkbox ise bu renk direkt ikona biner.
                  }
                },
                "& .MuiIconButton-root": {
                  color: "#ffffff !important",
                },
                "& .Byz-divider": {
                  backgroundColor: "rgba(255,255,255,0.3) !important",
                },
              },
              "& .MuiDataGrid-cell.Mui-selected": {
                backgroundColor: "#800000 !important",
                color: "#ffffff !important",
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: "#2196f3", // Satır seçili değilse bile tiklendiğinde mavi olsun
              },
            }}
          />
        </Box>

        {/* Muadil Liste - Daima Görünür */}
        <EquivalentList
          data={equivalentData}
        />
      </Box>

      {/* Seçtikleriniz Panel - her zaman açık, sol menünün üstünde, modal içinde alt kısımda */}
      <ChoicesPanel
        data={selectedStocksData}
        selectedItems={selectedStocksForPanel}
        config={{
          title: t("patient-request-service.selected-services.title") || "Seçtikleriniz",
          showAddToPatient: true,
          showFullScreenButton: true,
          showEditButton: true,
        }}
        columns={[
          { field: "name", headerName: t("menus.stock-selected-grid-stock-name") || "Stok Adı", flex: 1, minWidth: 180 },
          { field: "amount", headerName: t("menus.stock-selected-grid-amount") || "Miktar", width: 90 },
          { field: "unit", headerName: t("menus.stock-selected-grid-unit") || "Birimi", width: 90 },
          { field: "hTotal", headerName: t("menus.stock-selected-grid-h-total") || "H.Toplam", width: 110 },
          { field: "kTotal", headerName: t("menus.stock-selected-grid-k-total") || "K.Toplam", width: 110 },
          { field: "total", headerName: t("menus.stock-selected-grid-total") || "Toplam", width: 100 },
          { field: "doctor", headerName: t("menus.stock-selected-grid-doctor") || "Doktor", width: 130 },
          { field: "depleted", headerName: t("menus.stock-selected-grid-depleted") || "Tükendi", width: 90 },
          { field: "direction", headerName: t("menus.stock-selected-grid-direction") || "Yön", width: 80 },
          { field: "outOfPackage", headerName: t("menus.stock-selected-grid-out-of-package") || "Paket Dışı", width: 110 },
        ]}
        onEdit={(item) => {
          console.log("Updated item:", item);
          // Burada parent state'i güncellemek istersen ekleme yapabilirsin
        }}
        onItemToggle={handlePanelItemToggle}
        onSelectAll={handleSelectAll}
        onAddToPatientClick={() => {
          // Hastaya ekleme işlemi
        }}
        gridHeight="30vh"
        gridPageSize={10}
        position="absolute"
        bottom={20}
        left={20}
        right={20}
        zIndex={1400}
        disableFullScreen
      />
    </Box>
  );
};

export default StockAddLayout;


